import React, { useState, useEffect, useRef } from 'react';
import { getFilePathForMediaFilesWithSizeForStyleSettings } from 'backend/additionalInformation';
import { TLoaderType } from 'types';
import { useBlobDownload } from 'hooks/useBlobDownload';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import breadcrumbs from 'images/breadcrumbs.svg';
// import { createAppError } from 'utils/helpers';
import classNames from 'classnames';
import style from './VideoUploaderForStyleSettings.module.scss';

interface IVideoUploaderForStyleSettings {
	translationID: string; // if "" will show noImageIcon
	isMakeImageRequest: boolean;
	loaderSize: TLoaderType;
	isShowControls: boolean;
	noImageIdIcon?: string;
	filePath?: Nullable<string>;
}

const VideoUploaderForStyleSettings: React.FC<IVideoUploaderForStyleSettings> = ({
	translationID,
	isMakeImageRequest,
	loaderSize,
	isShowControls,
	noImageIdIcon = 'teaserDefaultAvatar',
	filePath = null,
}) => {
	const [isShowImage, setIsShowImage] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [actualPath, setActualPath] = useState<string>('');
	const componentMounted = useRef(true);
	const blobDownload = useBlobDownload({ filePath, fileExtension: 'mp4', fileName: 'styleVideo' });
	const videoClasses = classNames(style.video, { [style.fullWidth]: !isShowControls });

	const initialization = async () => {
		try {
			const response = await getFilePathForMediaFilesWithSizeForStyleSettings(translationID, 'Original');
			if (componentMounted.current) {
				setActualPath(response.data.preSignedUrl ? response.data.preSignedUrl : '');
			}
		} catch (err) {
			if (componentMounted.current) {
				setActualPath('error');
				setIsLoading(false);
				// const error = err as Error;
				// const appError = createAppError(error);
				// addAppError(appError);
			}
		}
	};

	const handleError = () => {
		setActualPath('error');
		setIsLoading(false);
	};

	useEffect(() => {
		if (isShowImage) {
			translationID !== '' ? initialization() : setIsLoading(false);
		}
	}, [isShowImage]);

	// ----- This is to avoid unnecessary photo requests ----- //
	useEffect(() => {
		if (!isShowImage && isMakeImageRequest) {
			setIsShowImage(true);
		}
	}, [isMakeImageRequest]);
	// ----- This is to avoid unnecessary photo requests ----- //

	useEffect(() => {
		return () => {
			componentMounted.current = false;
		};
	}, []);

	return (
		<div className={style.container} data-test="VideoUploaderForStyleSettings">
			{isLoading ? <LoaderWithProps loaderSize={loaderSize} /> : null}
			{actualPath !== 'error' && actualPath && (
				<div className={style.videoWrapper}>
					{filePath && (
						<div onClick={blobDownload} className={style.downloadOverlay}>
							<svg className={style.downloadIcon}>
								<use xlinkHref={`${breadcrumbs}#downloadArrow`} />
							</svg>
						</div>
					)}
					<video
						className={videoClasses}
						controls={isShowControls}
						src={actualPath}
						onLoadedData={() => setIsLoading(false)}
						onError={handleError}
					/>
				</div>
			)}
			{actualPath === '' ? (
				<div className={style.noImage}>
					<svg className={style.icon}>
						<use xlinkHref={`${breadcrumbs}#${noImageIdIcon}`} />
					</svg>
				</div>
			) : null}
			{actualPath === 'error' ? (
				<div className={style.noImage}>
					<div className={style.flexColumn}>
						<svg className={style.iconError}>
							<use xlinkHref={`${breadcrumbs}#brokenPicture`} />
						</svg>
						<div>Error video</div>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default VideoUploaderForStyleSettings;
